/**
 * Renderer-side personalization — the mirror of
 * `ADP.Surveys.Shared/Personalization/PersonalizationTokens.cs`.
 *
 * The server fills `{{recipient.*}}` / `{{candidate.*}}` when it serves a schema and
 * leaves `{{answers.<questionId>}}` (and `{{answers.<questionId>.label}}`) untouched,
 * because only the browser knows what the respondent answered. This module fills
 * those at answer time: in the copy of the screen being shown (title, description,
 * question titles, help, placeholders, option labels…) and in the request fields of
 * an `optionsSource` just before it is fetched, so one question's endpoint can depend
 * on an earlier answer.
 *
 * Same grammar, same fallback chain (answer → inline `|fallback` → declared
 * `survey.variables[].fallback` → nothing), same per-surface escaping, same
 * "verbatim in copy, empty in a request field" rule for a token nothing can fill.
 * Change one side, change the other.
 */
/** Group 1 is the token path; group 2 the optional inline fallback (stops at `}`). */
const TOKEN_PATTERN = /\{\{\s*([A-Za-z_][A-Za-z0-9_.\-]*)\s*(?:\|([^}]*))?\}\}/g;
export const ANSWER_TOKEN_PREFIX = 'answers.';
export const ANSWER_LABEL_SUFFIX = '.label';
export function mightContainTokens(text) {
    return typeof text === 'string' && text.includes('{{');
}
export function isAnswerToken(name) {
    return (typeof name === 'string' && name.startsWith(ANSWER_TOKEN_PREFIX) && name.length > ANSWER_TOKEN_PREFIX.length);
}
/** `answers.nps` and `answers.nps.label` both yield `nps`; null for any other token. */
export function answerTokenQuestionId(name) {
    if (!isAnswerToken(name))
        return null;
    let path = name.slice(ANSWER_TOKEN_PREFIX.length);
    if (path.endsWith(ANSWER_LABEL_SUFFIX))
        path = path.slice(0, -ANSWER_LABEL_SUFFIX.length);
    return path.length === 0 ? null : path;
}
/** Escapes a resolved value for its surface. Mirrors `PersonalizationTokens.Encode`. */
export function encodeForSurface(value, surface) {
    switch (surface) {
        case 'url':
        case 'formBody':
            return encodeURIComponent(value);
        case 'jsonBody':
            // JSON string escaping without the surrounding quotes — the author wrote those.
            return JSON.stringify(value).slice(1, -1);
        case 'headerValue':
            return value.replace(/[\r\n]/g, '');
        default:
            return value;
    }
}
/** Which escaping a request body wants, from its media type. Mirrors `BodySurface`. */
export function bodySurface(contentType) {
    const mediaType = (contentType ?? '').split(';')[0].trim().toLowerCase();
    if (mediaType === 'application/json' || mediaType.endsWith('+json'))
        return 'jsonBody';
    if (mediaType === 'application/x-www-form-urlencoded')
        return 'formBody';
    return 'rawBody';
}
/**
 * Replaces every token in `input`. Resolution per token: the context's value, then
 * the inline `|fallback`, then the context's declared fallback; a token none of those
 * fill stays verbatim in copy and becomes empty in a request field.
 */
export function substituteTokens(input, context, locale, surface = 'copy') {
    if (!mightContainTokens(input))
        return input;
    return input.replace(TOKEN_PATTERN, (match, name, inline) => {
        const trimmedInline = inline?.trim();
        const resolved = context.value(name, locale) ?? (trimmedInline ? trimmedInline : null) ?? context.fallback(name, locale);
        if (resolved === null)
            return surface === 'copy' ? match : '';
        return encodeForSurface(resolved, surface);
    });
}
/** Every distinct token path written in `text`. */
export function collectTokenNames(text) {
    if (!mightContainTokens(text))
        return [];
    const names = new Set();
    for (const match of text.matchAll(TOKEN_PATTERN))
        names.add(match[1]);
    return Array.from(names);
}
function pickLocale(value, locale, defaultLocale) {
    if (value == null)
        return null;
    if (typeof value === 'string')
        return value || null;
    const exact = value[locale];
    if (exact)
        return exact;
    if (defaultLocale && value[defaultLocale])
        return value[defaultLocale];
    for (const key of Object.keys(value))
        if (value[key])
            return value[key];
    return null;
}
function findQuestion(schema, questionId) {
    for (const screen of schema.screens) {
        for (const q of screen.questions ?? []) {
            if (q && q.id === questionId)
                return q;
        }
    }
    return undefined;
}
/** The stored answer as text: what BI sees. Empty answers count as unanswered. */
export function formatAnswerValue(raw) {
    if (raw === undefined || raw === null)
        return null;
    if (typeof raw === 'string')
        return raw.length === 0 ? null : raw;
    if (typeof raw === 'number' || typeof raw === 'boolean')
        return String(raw);
    if (Array.isArray(raw)) {
        const parts = raw.map(formatAnswerValue).filter((p) => p !== null);
        return parts.length === 0 ? null : parts.join(', ');
    }
    if (typeof raw === 'object') {
        // File answers record { name, size, type }; the name is the sensible text.
        const name = raw.name;
        if (typeof name === 'string' && name.length > 0)
            return name;
        return JSON.stringify(raw);
    }
    return String(raw);
}
function optionLabel(options, id, locale, defaultLocale) {
    const option = options?.find((o) => o.id === id);
    return option ? pickLocale(option.label, locale, defaultLocale) : null;
}
/** The answer as the respondent saw it: option labels, Yes/No words, locale-formatted dates. */
export function formatAnswerLabel(question, raw, locale, options) {
    const value = formatAnswerValue(raw);
    if (value === null || !question)
        return value;
    const defaultLocale = options.schema.defaultLocale;
    const labelFor = (id) => optionLabel(question.options, id, locale, defaultLocale) ??
        optionLabel(question.id ? options.lookupOptions?.(question.id) : undefined, id, locale, defaultLocale) ??
        id;
    switch (question.type) {
        case 'singleChoice':
        case 'dropdown':
        case 'navigationList':
            return labelFor(String(raw));
        case 'multiChoice':
            return Array.isArray(raw) ? raw.map((id) => labelFor(String(id))).join(', ') : labelFor(String(raw));
        case 'yesNo': {
            const yes = raw === true || raw === 'true';
            const declared = pickLocale(question[yes ? 'yesLabel' : 'noLabel'], locale, defaultLocale);
            return declared ?? (yes ? options.yesNoLabels?.yes ?? 'Yes' : options.yesNoLabels?.no ?? 'No');
        }
        case 'date':
        case 'dateTime':
        case 'bookingSlot': {
            const date = new Date(value);
            if (Number.isNaN(date.getTime()))
                return value;
            try {
                return question.type === 'date'
                    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date)
                    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
            }
            catch {
                return value;
            }
        }
        default:
            return value;
    }
}
/**
 * A token context over the respondent's answers. `answers.<id>` is the stored
 * value, `answers.<id>.label` the display form; declared fallbacks come from
 * `schema.variables`. Every other token name resolves to nothing here — the server
 * already had its turn with those.
 */
export function createAnswerContext(options) {
    const { schema, answers } = options;
    return {
        value(name, locale) {
            const questionId = answerTokenQuestionId(name);
            if (questionId === null)
                return null;
            const raw = answers[questionId];
            if (!name.endsWith(ANSWER_LABEL_SUFFIX))
                return formatAnswerValue(raw);
            return formatAnswerLabel(findQuestion(schema, questionId), raw, locale, options);
        },
        fallback(name, locale) {
            const declared = schema.variables?.find((v) => v.name?.trim() === name);
            return pickLocale(declared?.fallback, locale, schema.defaultLocale);
        },
    };
}
// ─── Typed walk over a screen's copy ─────────────────────────────────────────
/**
 * Every LocalizedString-typed property across the schema DTOs, by JSON name. Pinned
 * on the C# side by `PersonalizationAnswerTokenTests.LocalizedStringFields_MatchTheSdkWalkList`
 * — a new localized field fails that test until it is added here.
 */
export const LOCALIZED_KEYS = [
    'title',
    'description',
    'help',
    'placeholder',
    'lowLabel',
    'highLabel',
    'unit',
    'yesLabel',
    'noLabel',
    'label',
];
function isLocalizedString(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    return Object.values(value).every((v) => typeof v === 'string');
}
/** Substitutes inside each locale's text, resolving for THAT locale. Same object back when nothing changed. */
function substituteLocalized(value, context) {
    let changed = false;
    const next = {};
    for (const [locale, text] of Object.entries(value)) {
        const replaced = substituteTokens(text, context, locale, 'copy');
        if (replaced !== text)
            changed = true;
        next[locale] = replaced;
    }
    return changed ? next : value;
}
/** Copy-on-write rewrite of the localized keys on one object (question, option, screen). */
function substituteCopyFields(node, context) {
    let out = null;
    for (const key of LOCALIZED_KEYS) {
        const value = node[key];
        if (!isLocalizedString(value))
            continue;
        const replaced = substituteLocalized(value, context);
        if (replaced === value)
            continue;
        out ??= { ...node };
        out[key] = replaced;
    }
    return out ?? node;
}
function substituteQuestion(question, context) {
    let out = substituteCopyFields(question, context);
    const options = question['options'];
    if (Array.isArray(options)) {
        let optionsChanged = false;
        const nextOptions = options.map((option) => {
            if (option === null || typeof option !== 'object')
                return option;
            const replaced = substituteCopyFields(option, context);
            if (replaced !== option)
                optionsChanged = true;
            return replaced;
        });
        if (optionsChanged)
            out = { ...out, options: nextOptions };
    }
    return out;
}
/**
 * The screen with every answer token in its copy filled from `context` — title,
 * description, each question's localized fields and each option's label. Returns
 * the very same object when nothing changed, so memoised consumers stay stable.
 * Request fields (`optionsSource`) are deliberately left alone: those are
 * substituted at fetch time by `substituteRequestFields`, with request escaping.
 */
export function personalizeScreen(screen, context) {
    let out = substituteCopyFields(screen, context);
    const questions = screen.questions;
    if (Array.isArray(questions)) {
        let changed = false;
        const next = questions.map((q) => {
            if (q === null || typeof q !== 'object')
                return q;
            const replaced = substituteQuestion(q, context);
            if (replaced !== q)
                changed = true;
            return replaced;
        });
        if (changed)
            out = { ...out, questions: next };
    }
    return out;
}
/**
 * An options source with every token in its request fields filled: the URL
 * (percent-encoded), query-parameter values (raw — `buildOptionsUrl` encodes them),
 * header values (CR/LF stripped) and the body (escaped for its content type).
 * Returns the same object when nothing changed.
 */
export function substituteRequestFields(source, context, locale) {
    let out = null;
    const set = (key, value) => {
        out ??= { ...source };
        out[key] = value;
    };
    const url = substituteTokens(source.url, context, locale, 'url');
    if (url !== source.url)
        set('url', url);
    if (source.body != null) {
        const body = substituteTokens(source.body, context, locale, bodySurface(effectiveContentType(source)));
        if (body !== source.body)
            set('body', body);
    }
    const queryParams = substituteMapValues(source.queryParams, context, locale, 'queryValue');
    if (queryParams !== source.queryParams)
        set('queryParams', queryParams);
    const headers = substituteMapValues(source.headers, context, locale, 'headerValue');
    if (headers !== source.headers)
        set('headers', headers);
    return out ?? source;
}
/** The media type a body is sent as — explicit `contentType`, else JSON when there is a body. */
export function effectiveContentType(source) {
    const explicit = source.contentType?.trim();
    if (explicit)
        return explicit;
    return source.body != null ? 'application/json' : undefined;
}
function substituteMapValues(map, context, locale, surface) {
    if (!map)
        return map;
    let out = null;
    for (const [key, value] of Object.entries(map)) {
        const replaced = substituteTokens(value, context, locale, surface);
        if (replaced === value)
            continue;
        out ??= { ...map };
        out[key] = replaced;
    }
    return out ?? map;
}
//# sourceMappingURL=personalization.js.map